import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Send, RotateCcw } from 'lucide-react';
import MenuListingCard from '@/components/MenuListingCard';
import demoBud from '@/assets/demo-bud.jpg';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { parseDemoLine } from '@/lib/demoListingParser';
import Reveal from './Reveal';

const SAMPLE = 'Blue Dream, smalls, 40lbs, $250/lb, THC 24%, COA Attached';

/** Stable pseudo-random interest so the same listing always tells the same story. */
const interestFor = (name: string) => {
  let h = 0;
  for (const ch of name.trim().toLowerCase()) h = (h * 31 + ch.charCodeAt(0)) % 9973;
  return { opened: 3 + (h % 9), marks: 2 + (h % 4) };
};

/**
 * The proof block: a visitor pastes a line the way a supplier sends it and sees
 * the listing card Cannafax builds. Entirely client-side — nothing leaves the page.
 */
const InterestDemo = () => {
  const [line, setLine] = useState(SAMPLE);
  const [sent, setSent] = useState(false);
  const listing = useMemo(() => parseDemoLine(line), [line]);
  const interest = listing ? interestFor(listing.strain_name) : null;

  return (
    <section className="border-b border-border bg-background py-20 md:py-24">
      <div className="container-wide grid gap-12 lg:grid-cols-2 lg:gap-16">
        <Reveal>
          <div className="flex h-full flex-col justify-center">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            See it with your own listings
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Paste a line the way a supplier would send it. Cannafax turns it into a listing your
            buyers can actually use.
          </p>

          <Label htmlFor="demo-line" className="mt-8 block text-sm font-medium">
            Supplier line
          </Label>
          <Textarea
            id="demo-line"
            value={line}
            maxLength={160}
            rows={2}
            className="mt-2"
            onChange={(e) => {
              setLine(e.target.value);
              setSent(false);
            }}
          />

          <div className="mt-5 flex flex-wrap gap-3">
            <Button
              onClick={() => setSent(true)}
              disabled={!listing || sent}
              className="bg-brand text-brand-foreground hover:bg-brand/90"
            >
              <Send className="mr-2 h-4 w-4" />
              Send to buyers
            </Button>
            {(sent || line !== SAMPLE) && (
              <Button
                variant="quiet"
                onClick={() => {
                  setLine(SAMPLE);
                  setSent(false);
                }}
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Reset
              </Button>
            )}
          </div>

          <p className="mt-4 text-xs text-muted-foreground">
            Illustration only, nothing is saved or sent.
          </p>
          </div>
        </Reveal>

        <Reveal delay={80}>
          <div className="flex h-full min-h-[420px] flex-col rounded-xl border border-border bg-subtle p-6 sm:p-8">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Your listing
            </p>

            {!listing ? (
              <div className="flex flex-1 items-center justify-center">
                <p className="max-w-xs text-center text-sm text-muted-foreground">
                  Start the line with a strain name, then add form, quantity, price and THC.
                </p>
              </div>
            ) : (
              <div className="mt-6 flex flex-1 flex-col items-center justify-center">
                <div className="w-full max-w-[300px]">
                  {/* The exact supplier-menu card buyers see — one source, never a lookalike. */}
                  <MenuListingCard
                    item={{ ...listing, images: [demoBud] }}
                    variant="tile"
                    onCardClick={() => {}}
                    onContactClick={() => {}}
                  />
                  {sent && interest && (
                    <div className="mt-4 flex items-center justify-between gap-3">
                      <p className="text-sm text-muted-foreground">
                        {interest.opened} buyers opened it
                      </p>
                      <div className="flex items-center gap-1" aria-hidden="true">
                        {Array.from({ length: interest.marks }).map((_, m) => (
                          <span
                            key={m}
                            style={{ animationDelay: `${200 + m * 140}ms` }}
                            className="circle-2 bg-brand motion-safe:animate-mark-settle"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {sent && (
                  <Button asChild size="lg" className="mt-6 bg-brand text-brand-foreground hover:bg-brand/90">
                    <Link to="/auth?mode=signup">
                      Build this for real, free
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default InterestDemo;
